import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, BookOpen, Sparkles, MessageSquare, Heart, Share2, Users2, TrendingUp } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API_URL as API } from '@/utils/apiConfig';

/**
 * Behavior analytics — deep dive into what users actually do
 * inside the app. Rendered as compact cards + a top-plans table so
 * an admin can eyeball engagement trends without a query builder.
 */
function StatCard({ icon: Icon, label, value, sub, accent = '#6366f1', testId }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'var(--admin-card-bg, #ffffff08)', border: '1px solid var(--admin-border, #ffffff14)' }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-3xl font-bold" style={{ color: 'var(--admin-text, #fff)' }}>
        {typeof value === 'number' ? value.toLocaleString() : (value ?? '–')}
      </div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Section({ title, description, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--admin-text, #fff)' }}>{title}</h2>
      {description && (
        <p className="text-sm mb-4" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>{description}</p>
      )}
      {children}
    </section>
  );
}

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics/behavior`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Failed to load analytics');
      }
    })();
  }, []);

  if (!data && !error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="text-center py-24">
          <p className="text-sm" style={{ color: '#ef4444' }}>{String(error)}</p>
        </div>
      </AdminLayout>
    );
  }

  const p = data.plans;
  const c = data.custom_plans;
  const s = data.sharing;
  const e = data.engagement;
  const ch = data.chat;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--admin-text, #fff)' }}>
            Behavior Analytics
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>
            What people are actually doing across the app. Excludes {data.excluded_users} internal accounts.
          </p>
        </div>

        <Section
          title="Study Plans"
          description="Enrollments, completions and the plans that are getting real traction."
          testId="analytics-section-plans"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard icon={BookOpen} label="Total enrollments" value={p.total_enrollments} accent="#6366f1" testId="stat-total-enrollments" />
            <StatCard icon={TrendingUp} label="Active now" value={p.active_enrollments} accent="#22c55e" testId="stat-active-enrollments" />
            <StatCard icon={Sparkles} label="Completions" value={p.completed_enrollments} sub={`${p.completion_rate_pct}% completion rate`} accent="#eab308" testId="stat-completions" />
            <StatCard icon={Users2} label="New (7d / 30d)" value={`${p.enrollments_last_7_days} / ${p.enrollments_last_30_days}`} accent="#06b6d4" testId="stat-new-enrollments" />
          </div>
          {p.top_plans.length > 0 && (
            <div
              className="rounded-2xl p-4"
              style={{ background: 'var(--admin-card-bg, #ffffff08)', border: '1px solid var(--admin-border, #ffffff14)' }}
              data-testid="analytics-top-plans-table"
            >
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>
                Top 10 plans by enrollment
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>
                    <th className="text-left py-1">Title</th>
                    <th className="text-left py-1">Category</th>
                    <th className="text-right py-1">Enrolled</th>
                    <th className="text-right py-1">Completed</th>
                    <th className="text-right py-1">Rate</th>
                  </tr>
                </thead>
                <tbody style={{ color: 'var(--admin-text, #fff)' }}>
                  {p.top_plans.map((row) => (
                    <tr key={row.plan_id} className="border-t" style={{ borderColor: 'var(--admin-border, #ffffff14)' }}>
                      <td className="py-2">
                        {row.title}
                        {row.is_custom && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#6366f133', color: '#818cf8' }}>Custom</span>
                        )}
                      </td>
                      <td className="py-2 text-xs" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>{row.category || '–'}</td>
                      <td className="py-2 text-right">{row.enrollments}</td>
                      <td className="py-2 text-right">{row.completions}</td>
                      <td className="py-2 text-right">{row.enrollments > 0 ? `${Math.round((row.completions / row.enrollments) * 100)}%` : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section title="Custom Plans" description="Plans Eili built for paying users on demand." testId="analytics-section-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard icon={Sparkles} label="Total built" value={c.total} sub={`${c.ready} ready · ${c.building} building · ${c.failed} failed`} accent="#a855f7" testId="stat-custom-total" />
            <StatCard icon={TrendingUp} label="Built (7d)" value={c.created_last_7_days} sub={`${c.created_last_30_days} in last 30 days`} accent="#22c55e" testId="stat-custom-recent" />
            <StatCard icon={BookOpen} label="Length mix" value={Object.entries(c.length_breakdown).map(([d, n]) => `${d}d:${n}`).join('  ') || '–'} accent="#06b6d4" testId="stat-custom-length" />
            <StatCard icon={TrendingUp} label="Cadence mix" value={Object.entries(c.cadence_breakdown).map(([d, n]) => `${d}:${n}`).join('  ') || '–'} accent="#eab308" testId="stat-custom-cadence" />
          </div>
        </Section>

        <Section title="Sharing & Virality" description="How custom plans are spreading between users." testId="analytics-section-sharing">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard icon={Share2} label="Plans shared" value={s.shared_plans} accent="#f97316" testId="stat-shared" />
            <StatCard icon={Users2} label="Accepts" value={s.share_accepts} sub="Total friends who joined a shared plan" accent="#22c55e" testId="stat-accepts" />
            <StatCard icon={TrendingUp} label="Viral coefficient" value={s.viral_coefficient} sub="Accepts per shared plan" accent="#a855f7" testId="stat-viral" />
          </div>
        </Section>

        <Section title="Content Engagement" description="Favorites, saves and highlights across the app." testId="analytics-section-engagement">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard icon={Heart} label="Reflection favorites" value={e.reflection_favorites} sub={`+${e.reflection_favorites_last_7_days} this week`} accent="#ec4899" testId="stat-refl-fav" />
            <StatCard icon={Sparkles} label="DYK saves" value={e.dyk_saves} sub={`+${e.dyk_saves_last_7_days} this week`} accent="#a855f7" testId="stat-dyk" />
            <StatCard icon={BookOpen} label="Bible highlights" value={e.bible_highlights} sub={`+${e.bible_highlights_last_7_days} this week`} accent="#f97316" testId="stat-highlights" />
            <StatCard icon={Share2} label="Saved shares" value={e.saved_shares} accent="#06b6d4" testId="stat-saved-shares" />
          </div>
          {Object.keys(e.reflection_audience_last_30_days || {}).length > 0 && (
            <div
              className="rounded-2xl p-4"
              style={{ background: 'var(--admin-card-bg, #ffffff08)', border: '1px solid var(--admin-border, #ffffff14)' }}
              data-testid="analytics-audience-breakdown"
            >
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>
                Reflection views by audience (last 30 days)
              </p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(e.reflection_audience_last_30_days).map(([aud, count]) => (
                  <div key={aud} className="px-3 py-1.5 rounded-full text-sm" style={{ background: 'var(--admin-hover, #ffffff11)', color: 'var(--admin-text, #fff)' }}>
                    <span className="uppercase text-xs opacity-70">{aud}</span>{' '}<span className="font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Chat" description="Message volume and active chatters." testId="analytics-section-chat">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={MessageSquare} label="Total user messages" value={ch.total_user_messages} accent="#6366f1" testId="stat-chat-total" />
            <StatCard icon={MessageSquare} label="Messages (7d)" value={ch.messages_last_7_days} accent="#22c55e" testId="stat-chat-week" />
            <StatCard icon={MessageSquare} label="Messages (24h)" value={ch.messages_last_24_hours} accent="#f97316" testId="stat-chat-day" />
            <StatCard icon={Users2} label="Unique chatters (7d)" value={ch.unique_chatters_last_7_days} accent="#a855f7" testId="stat-chat-unique" />
          </div>
        </Section>

        <p className="text-[11px] text-right mt-6" style={{ color: 'var(--admin-text-secondary, #ffffff88)' }}>
          Snapshot generated {new Date(data.generated_at).toLocaleString()}
        </p>
      </div>
    </AdminLayout>
  );
}
